import { ComponentProps, ReactNode, useCallback, useState } from 'react';
import { ButtonV2 } from '@bridebook/ui';
import { InterceptModalContent } from './intercept-modal-content';

export type InterceptModalAutoCloseProps = {
  title: string;
  text?: string | ReactNode;
  primaryButtonText: string;
  /**
   * Function to be called when the primary button is clicked.
   * If the function returns a promise, the button will be shown as loading until the promise is resolved.
   */
  onPrimaryButtonClick: () => void | Promise<void>;
  secondaryButtonText: string;
  /**
   * Function to be called when the secondary button is clicked.
   * If the function returns a promise, the button will be shown as loading until the promise is resolved.
   * It is optional, because in most cases secondary button only close the modal. But if you need something else (like analytics) you can use this.
   */
  onSecondaryButtonClick?: () => void | Promise<void>;
  /**
   * Props for the primary button.
   */
  primaryButtonProps?: ComponentProps<typeof ButtonV2>;
  /**
   * Props for the secondary button.
   */
  secondaryButtonProps?: ComponentProps<typeof ButtonV2>;
  closeModal: () => void | Promise<void>;
  image?: ReactNode;
};

/**
 * A wrapper component for InterceptModalContent that handles automatic modal closing and loading states.
 *
 * Key features:
 * - Automatically closes modal after primary/secondary actions complete
 * - Manages loading states for both buttons during async operations
 * - Handles both sync and async button click handlers
 */

export const InterceptModalAutoClose = ({
  title,
  text,
  primaryButtonText,
  onPrimaryButtonClick,
  secondaryButtonText,
  onSecondaryButtonClick,
  primaryButtonProps,
  secondaryButtonProps,
  closeModal,
  image,
}: InterceptModalAutoCloseProps) => {
  const [isLoadingPrimaryButton, setIsLoadingPrimaryButton] = useState(false);
  const [isLoadingSecondaryButton, setIsLoadingSecondaryButton] = useState(false);

  const handlePrimaryButtonClick = useCallback(async () => {
    setIsLoadingPrimaryButton(true);
    await onPrimaryButtonClick();
    setIsLoadingPrimaryButton(false);
    closeModal();
  }, [onPrimaryButtonClick, closeModal]);

  const handleSecondaryButtonClick = useCallback(async () => {
    setIsLoadingSecondaryButton(true);
    await onSecondaryButtonClick?.();
    setIsLoadingSecondaryButton(false);
    closeModal();
  }, [onSecondaryButtonClick, closeModal]);

  return (
    <InterceptModalContent
      title={title}
      text={text}
      primaryButtonProps={{
        onClick: handlePrimaryButtonClick,
        loading: isLoadingPrimaryButton,
        children: primaryButtonText,
        ...primaryButtonProps,
      }}
      secondaryButtonProps={{
        onClick: handleSecondaryButtonClick,
        loading: isLoadingSecondaryButton,
        children: secondaryButtonText,
        ...secondaryButtonProps,
      }}
      image={image}
    />
  );
};
